import React from "react";
import { Checkbox, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";
import { LoadingButton, Modal } from "@rentablo/components";
import { Option } from "./deriveContraAccounts";
import { GroupedAccountList } from "./GroupedAccountList";

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
});

const ScrollableList = styled("div")(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  paddingBottom: theme.spacing(2),
}));

const Footer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(4),
  borderTop: `1px solid ${theme.palette.border.secondary}`,
}));

export interface SelectAdditionalContraAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: Option[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  onSubmit: () => void;
  isFetching: boolean;
}

export const SelectAdditionalContraAccountsModal = ({
  isOpen,
  onClose,
  options,
  selectedIds,
  onToggle,
  onSubmit,
  isFetching,
}: SelectAdditionalContraAccountsModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("createAndEditAccount.additionalContraAccounts.title")}
      subtitle={t("createAndEditAccount.additionalContraAccounts.subtitle")}
      testId="select-additional-contra-accounts"
      maxHeight="100%"
    >
      <Container>
        <ScrollableList>
          <GroupedAccountList
            items={options}
            isSelected={(id) => selectedIds.includes(id)}
            renderControl={(id, selected) => (
              <Checkbox
                checked={selected}
                onChange={() => onToggle(id)}
                data-testid={`checkbox-${id}`}
                sx={{ p: 0, mr: 1, ml: -2, flexShrink: 0 }}
              />
            )}
          />
        </ScrollableList>
        <Footer>
          <LoadingButton
            variant={theme.bankConnection?.button?.variant || "primary"}
            fullWidth
            isFetching={isFetching}
            onClick={onSubmit}
            data-testid="submit-button"
          >
            {t("selectContraAccount.nextButton")}
          </LoadingButton>
        </Footer>
      </Container>
    </Modal>
  );
};
